import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, CircularProgress,
  Alert, Tooltip, Divider, LinearProgress, IconButton,
} from '@mui/material';
import {
  Refresh, MyLocation, WbTwilight, Explore, Visibility,
  NightsStay, Height, Speed, Public, Schedule,
} from '@mui/icons-material';
import { moonAPI } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/** Route apod.nasa.gov image URLs through our backend proxy to avoid browser blocks. */
const apodImageSrc = (url) => {
  if (url && /^https?:\/\/apod\.nasa\.gov\//i.test(url)) {
    return `${API_BASE}/moon/apod-image?url=${encodeURIComponent(url)}`;
  }
  return url;
};

// ── Moon glow keyframes ───────────────────────────────────────────────────────
const GLOW_PULSE = `
  @keyframes moonGlow {
    0%,100% { box-shadow: 0 0 30px 10px rgba(255,236,153,0.3), 0 0 60px 20px rgba(255,236,153,0.15); }
    50%      { box-shadow: 0 0 50px 20px rgba(255,236,153,0.5), 0 0 90px 35px rgba(255,236,153,0.25); }
  }
  @keyframes rotateSlow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes shimmer {
    0%,100% { opacity: 0.6; } 50% { opacity: 1; }
  }
  @keyframes compassPulse {
    0%,100% { opacity: 0.7; } 50% { opacity: 1; }
  }
`;

// ── Surface features data ─────────────────────────────────────────────────────
const SURFACE_FEATURES = [
  {
    name: 'Tycho Crater',
    emoji: '🕳️',
    coords: '43.3°S, 11.2°W',
    diameter: '85 km',
    description:
      'One of the most prominent craters on the Moon. Named after Tycho Brahe, it formed ~108 Mya and features stunning ray systems extending thousands of km.',
    color: '#ef9a9a',
  },
  {
    name: 'Copernicus Crater',
    emoji: '🌑',
    coords: '9.7°N, 20.1°W',
    diameter: '93 km',
    description:
      'Called the "Monarch of the Moon". A prominent impact crater ~800 million years old with terraced walls and a complex central peak.',
    color: '#80cbc4',
  },
  {
    name: 'Mare Tranquillitatis',
    emoji: '🌊',
    coords: '8.5°N, 31.4°E',
    diameter: '873 km',
    description:
      'Sea of Tranquility — the historic Apollo 11 landing site where humans first set foot on the Moon on July 20, 1969.',
    color: '#90caf9',
  },
  {
    name: 'Oceanus Procellarum',
    emoji: '🌙',
    coords: '18.4°N, 57.4°W',
    diameter: '2500 km',
    description:
      'Ocean of Storms — the largest lunar mare, covering about 2.5 million km². Home to Apollo 12 and Luna 9 landing sites.',
    color: '#ce93d8',
  },
  {
    name: 'Montes Apenninus',
    emoji: '⛰️',
    coords: '19.9°N, 2.5°W',
    diameter: '600 km',
    description:
      'The Lunar Apennines — a magnificent mountain range reaching heights of 5 km. Forms the south-eastern border of Mare Imbrium.',
    color: '#a5d6a7',
  },
  {
    name: 'Mare Imbrium',
    emoji: '🌑',
    coords: '32.8°N, 15.6°W',
    diameter: '1145 km',
    description:
      'Sea of Rains — a vast impact basin formed 3.9 billion years ago. Apollo 15 landed near its eastern edge.',
    color: '#ffcc80',
  },
];

// ── Compass component ─────────────────────────────────────────────────────────
function CompassCard({ azimuth, direction, altitude }) {
  const size  = 160;
  const cx    = size / 2;
  const cy    = size / 2;
  const r     = 64;
  const needleAngle = azimuth - 90; // rotate so 0° = North = top

  const cardinals = ['N', 'E', 'S', 'W'];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <svg width={size} height={size}>
        {/* Outer ring */}
        <circle cx={cx} cy={cy} r={r + 6} fill="none" stroke="rgba(255,236,153,0.15)" strokeWidth={2} />
        {/* Tick marks */}
        {Array.from({ length: 36 }, (_, i) => {
          const a = ((i * 10) - 90) * Math.PI / 180;
          const isMajor = i % 9 === 0;
          const r1 = r - (isMajor ? 12 : 6);
          const r2 = r;
          return (
            <line
              key={i}
              x1={cx + r1 * Math.cos(a)} y1={cy + r1 * Math.sin(a)}
              x2={cx + r2 * Math.cos(a)} y2={cy + r2 * Math.sin(a)}
              stroke={isMajor ? 'rgba(255,236,153,0.6)' : 'rgba(255,236,153,0.2)'}
              strokeWidth={isMajor ? 2 : 1}
            />
          );
        })}
        {/* Cardinal labels */}
        {cardinals.map((label, i) => {
          const a = (i * 90 - 90) * Math.PI / 180;
          return (
            <text
              key={label}
              x={cx + (r - 22) * Math.cos(a)}
              y={cy + (r - 22) * Math.sin(a) + 4}
              textAnchor="middle"
              fill={label === 'N' ? '#ef9a9a' : 'rgba(255,236,153,0.7)'}
              fontSize={label === 'N' ? 13 : 11}
              fontWeight={label === 'N' ? 700 : 400}
              fontFamily="Orbitron, monospace"
            >
              {label}
            </text>
          );
        })}
        {/* Compass needle */}
        <g transform={`rotate(${needleAngle}, ${cx}, ${cy})`}>
          {/* North (yellow) */}
          <polygon
            points={`${cx},${cy - r + 18} ${cx - 7},${cy + 8} ${cx + 7},${cy + 8}`}
            fill="rgba(255,236,153,0.9)"
          />
          {/* South (dim) */}
          <polygon
            points={`${cx},${cy + r - 18} ${cx - 7},${cy - 8} ${cx + 7},${cy - 8}`}
            fill="rgba(255,255,255,0.2)"
          />
        </g>
        {/* Center dot */}
        <circle cx={cx} cy={cy} r={5} fill="#ffeccc" />
        <circle cx={cx} cy={cy} r={3} fill="#ffcc44" />
      </svg>
      <Typography variant="h5" sx={{ color: '#ffeccc', fontFamily: 'Orbitron, monospace', fontWeight: 700 }}>
        {direction}
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,236,153,0.6)' }}>
        Az: {azimuth}° · Alt: {altitude}°
      </Typography>
    </Box>
  );
}

// ── Moon phase visual ─────────────────────────────────────────────────────────
function MoonVisual({ illumination, phase }) {
  const size = 180;
  const r    = 80;
  const cx   = size / 2;
  const cy   = size / 2;

  // Calculate the terminator path for the illumination fraction
  const illumFrac = illumination / 100;
  const isWaning  = phase.includes('Waning') || phase === 'Last Quarter';

  // x-offset of the terminator ellipse center from circle center
  const termOffset = (2 * illumFrac - 1) * r;

  // SVG path for the lit portion (right or left half + ellipse)
  let litPath;
  if (illumFrac <= 0.01) {
    litPath = ''; // New moon
  } else if (illumFrac >= 0.99) {
    litPath = `M ${cx - r} ${cy} a ${r} ${r} 0 0 1 ${2*r} 0 a ${r} ${r} 0 0 1 ${-2*r} 0`; // Full
  } else {
    const ellipseRx = Math.abs(termOffset);
    const termDir   = isWaning ? 1 : -1;
    litPath = [
      `M ${cx} ${cy - r}`,
      `A ${r} ${r} 0 0 ${isWaning ? 0 : 1} ${cx} ${cy + r}`,
      `A ${ellipseRx} ${r} 0 0 ${termDir > 0 ? 1 : 0} ${cx} ${cy - r}`,
      'Z',
    ].join(' ');
  }

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'moonGlow 3s ease-in-out infinite',
      borderRadius: '50%',
      width: size, height: size,
    }}>
      <svg width={size} height={size}>
        <defs>
          <radialGradient id="moonBase" cx="40%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#f5f0e0" />
            <stop offset="60%" stopColor="#d4c9a0" />
            <stop offset="100%" stopColor="#8a7d60" />
          </radialGradient>
          <radialGradient id="moonDark" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1a1a2e" />
            <stop offset="100%" stopColor="#0a0a1a" />
          </radialGradient>
          <filter id="moonBlur">
            <feGaussianBlur stdDeviation="1.5" />
          </filter>
        </defs>
        {/* Dark base (shadow side) */}
        <circle cx={cx} cy={cy} r={r} fill="url(#moonDark)" />
        {/* Lit portion */}
        {litPath && <path d={litPath} fill="url(#moonBase)" />}
        {/* Subtle craters */}
        <circle cx={cx - 20} cy={cy + 15} r={8}  fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth={1} />
        <circle cx={cx + 25} cy={cy - 20} r={5}  fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth={1} />
        <circle cx={cx - 5}  cy={cy - 30} r={11} fill="none" stroke="rgba(0,0,0,0.1)"  strokeWidth={1} />
        <circle cx={cx + 15} cy={cy + 25} r={7}  fill="none" stroke="rgba(0,0,0,0.1)"  strokeWidth={1} />
        {/* Glow ring */}
        <circle cx={cx} cy={cy} r={r} fill="none"
          stroke="rgba(255,236,153,0.3)" strokeWidth={3} filter="url(#moonBlur)" />
      </svg>
    </Box>
  );
}

// ── Stat row helper ───────────────────────────────────────────────────────────
function StatRow({ label, value, icon, color = '#ffeccc' }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && React.cloneElement(icon, { sx: { fontSize: 16, color: 'rgba(255,236,153,0.5)' } })}
        <Typography variant="body2" sx={{ color: 'rgba(255,236,153,0.6)', fontFamily: 'Exo 2, sans-serif' }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color, fontWeight: 600, fontFamily: 'Orbitron, monospace', fontSize: 13 }}>
        {value}
      </Typography>
    </Box>
  );
}

// ── Card wrapper ──────────────────────────────────────────────────────────────
function MoonCard({ title, icon, children, sx = {} }) {
  return (
    <Card sx={{
      bgcolor: 'rgba(10,22,40,0.95)',
      border: '1px solid rgba(255,236,153,0.12)',
      borderRadius: 3,
      transition: 'border-color 0.3s, box-shadow 0.3s',
      '&:hover': {
        borderColor: 'rgba(255,236,153,0.3)',
        boxShadow: '0 4px 30px rgba(255,236,153,0.08)',
      },
      ...sx,
    }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {title && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {icon && React.cloneElement(icon, { sx: { color: 'rgba(255,236,153,0.7)', fontSize: 20 } })}
            <Typography variant="subtitle2" sx={{
              color: 'rgba(255,236,153,0.8)',
              fontFamily: 'Orbitron, monospace',
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              fontSize: 11,
            }}>
              {title}
            </Typography>
          </Box>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MoonPage() {
  const [moonData,    setMoonData]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [location,    setLocation]    = useState({ lat: 0, lon: 0 });
  const [locLabel,    setLocLabel]    = useState('Global Average');
  const [locLoading,  setLocLoading]  = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);

  const fetchMoon = useCallback(async (lat = location.lat, lon = location.lon) => {
    try {
      setLoading(true);
      setError(null);
      const res = await moonAPI.getData(lat, lon);
      setMoonData(res.data);
      setLastRefresh(new Date());
    } catch (err) {
      setError('Unable to load Moon data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [location.lat, location.lon]);

  // Detect user location
  const detectLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocLabel('Location unavailable');
      return;
    }
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = parseFloat(pos.coords.latitude.toFixed(4));
        const lon = parseFloat(pos.coords.longitude.toFixed(4));
        setLocation({ lat, lon });
        setLocLabel(`${lat}°, ${lon}°`);
        setLocLoading(false);
        fetchMoon(lat, lon);
      },
      () => {
        setLocLabel('Location denied — using global');
        setLocLoading(false);
      }
    );
  }, [fetchMoon]);

  useEffect(() => {
    fetchMoon(0, 0);
    // Auto-refresh every hour
    intervalRef.current = setInterval(() => fetchMoon(location.lat, location.lon), 3600_000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moon = moonData;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#000000', pb: 6, position: 'relative' }}>
      <style>{GLOW_PULSE}</style>

      {/* Starfield background */}
      <Box sx={{
        position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none',
        background: 'radial-gradient(ellipse at 60% 30%, rgba(255,236,153,0.03) 0%, transparent 60%)',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, pt: 3 }}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Typography sx={{ fontSize: 40, lineHeight: 1 }}>🌕</Typography>
              <Box>
                <Typography variant="h4" sx={{
                  fontFamily: 'Orbitron, monospace', fontWeight: 900,
                  background: 'linear-gradient(135deg, #ffeccc 0%, #ffd54f 50%, #ffb300 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  letterSpacing: 2,
                }}>
                  MOON DASHBOARD
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,236,153,0.5)', fontFamily: 'Exo 2, sans-serif' }}>
                  Real-time lunar data · Phase · Position · Astronomy
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip
              icon={locLoading ? <CircularProgress size={14} sx={{ color: '#ffeccc' }} /> : <MyLocation sx={{ fontSize: '14px !important', color: '#ffeccc !important' }} />}
              label={locLabel}
              onClick={detectLocation}
              size="small"
              sx={{
                bgcolor: 'rgba(255,236,153,0.1)',
                color: '#ffeccc',
                border: '1px solid rgba(255,236,153,0.2)',
                cursor: 'pointer',
                fontFamily: 'Exo 2, sans-serif',
                '&:hover': { bgcolor: 'rgba(255,236,153,0.18)' },
              }}
            />
            <IconButton
              size="small"
              onClick={() => fetchMoon(location.lat, location.lon)}
              disabled={loading}
              sx={{ color: 'rgba(255,236,153,0.6)', '&:hover': { color: '#ffeccc' } }}
            >
              <Refresh fontSize="small" />
            </IconButton>
            {lastRefresh && (
              <Typography variant="caption" sx={{ color: 'rgba(255,236,153,0.3)', fontFamily: 'Exo 2, sans-serif' }}>
                {lastRefresh.toLocaleTimeString()}
              </Typography>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 3, bgcolor: 'rgba(255,167,38,0.1)', color: '#ffa726', border: '1px solid rgba(255,167,38,0.3)' }}>
            {error}
          </Alert>
        )}

        {loading && !moon ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12, gap: 3 }}>
            <Box sx={{ fontSize: 80, animation: 'shimmer 1.5s ease-in-out infinite' }}>🌕</Box>
            <CircularProgress sx={{ color: '#ffeccc' }} />
            <Typography sx={{ color: 'rgba(255,236,153,0.5)', fontFamily: 'Exo 2, sans-serif' }}>
              Computing lunar ephemeris…
            </Typography>
          </Box>
        ) : moon && (
          <Grid container spacing={3}>

            {/* ── Moon Overview (Phase + Visual) ─────────────────────── */}
            <Grid item xs={12} md={5}>
              <MoonCard sx={{ height: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <MoonVisual illumination={moon.illumination} phase={moon.phase} />

                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 36, lineHeight: 1 }}>{moon.phaseIcon}</Typography>
                    <Typography variant="h5" sx={{
                      fontFamily: 'Orbitron, monospace', fontWeight: 800,
                      color: '#ffeccc', mt: 0.5,
                    }}>
                      {moon.phase}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,236,153,0.5)', mt: 0.5 }}>
                      Age: {moon.phaseAge} days
                    </Typography>
                  </Box>

                  {/* Illumination bar */}
                  <Box sx={{ width: '100%', px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,236,153,0.5)' }}>Illumination</Typography>
                      <Typography variant="caption" sx={{ color: '#ffeccc', fontWeight: 700, fontFamily: 'Orbitron, monospace' }}>
                        {moon.illumination}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={moon.illumination}
                      sx={{
                        height: 8, borderRadius: 4,
                        bgcolor: 'rgba(255,236,153,0.1)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          background: 'linear-gradient(90deg, #ffa000, #ffeccc)',
                        },
                      }}
                    />
                  </Box>

                  {/* Phase strip */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', px: 0.5 }}>
                    {['🌑','🌒','🌓','🌔','🌕','🌖','🌗','🌘'].map((emoji, i) => (
                      <Tooltip key={i} title={['New','Wax.Crescent','First Qtr','Wax.Gibbous','Full','Wan.Gibbous','Last Qtr','Wan.Crescent'][i]}>
                        <Typography sx={{
                          fontSize: 20, cursor: 'default',
                          opacity: moon.phaseIcon === emoji ? 1 : 0.35,
                          transform: moon.phaseIcon === emoji ? 'scale(1.4)' : 'scale(1)',
                          transition: 'all 0.2s',
                        }}>
                          {emoji}
                        </Typography>
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              </MoonCard>
            </Grid>

            {/* ── Right column top ──────────────────────────────────── */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={3}>

                {/* Position & Distance */}
                <Grid item xs={12} sm={6}>
                  <MoonCard title="Position & Distance" icon={<Public />} sx={{ height: '100%' }}>
                    <StatRow label="Distance"  value={`${moon.distance?.toLocaleString()} km`} icon={<Speed />}    color="#ffd54f" />
                    <Divider sx={{ borderColor: 'rgba(255,236,153,0.06)', my: 0.5 }} />
                    <StatRow label="Sub-Lat"   value={`${moon.subLunarLat}°`}              icon={<Height />}   />
                    <StatRow label="Sub-Lon"   value={`${moon.subLunarLon}°`}              icon={<Height />}   />
                    <Divider sx={{ borderColor: 'rgba(255,236,153,0.06)', my: 0.5 }} />
                    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,236,153,0.04)', border: '1px solid rgba(255,236,153,0.08)' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,236,153,0.4)', display: 'block', mb: 0.5 }}>
                        Distance range
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,236,153,0.4)' }}>Perigee<br />363,300 km</Typography>
                        <LinearProgress
                          variant="determinate"
                          value={((moon.distance - 363300) / (405500 - 363300)) * 100}
                          sx={{
                            flex: 1, mx: 1, height: 4, borderRadius: 2,
                            bgcolor: 'rgba(255,236,153,0.1)',
                            '& .MuiLinearProgress-bar': { bgcolor: '#ffd54f', borderRadius: 2 },
                          }}
                        />
                        <Typography variant="caption" sx={{ color: 'rgba(255,236,153,0.4)', textAlign: 'right' }}>Apogee<br />405,500 km</Typography>
                      </Box>
                    </Box>
                  </MoonCard>
                </Grid>

                {/* Rise & Set */}
                <Grid item xs={12} sm={6}>
                  <MoonCard title="Rise & Set" icon={<WbTwilight />} sx={{ height: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,167,38,0.08)', border: '1px solid rgba(255,167,38,0.15)', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,167,38,0.7)' }}>🌅 Moonrise</Typography>
                        <Typography variant="h6" sx={{ color: '#ffb74d', fontFamily: 'Orbitron, monospace', fontWeight: 700, mt: 0.25 }}>
                          {moon.moonrise}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(79,99,247,0.08)', border: '1px solid rgba(79,99,247,0.15)', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(130,148,255,0.7)' }}>🌄 Moonset</Typography>
                        <Typography variant="h6" sx={{ color: '#9fa8da', fontFamily: 'Orbitron, monospace', fontWeight: 700, mt: 0.25 }}>
                          {moon.moonset}
                        </Typography>
                      </Box>
                      <Divider sx={{ borderColor: 'rgba(255,236,153,0.06)' }} />
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,236,153,0.04)' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,236,153,0.4)', display: 'block' }}>
                          <Schedule sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                          Best Viewing Window
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#ffeccc', fontFamily: 'Orbitron, monospace', mt: 0.5, fontSize: 11 }}>
                          {moon.bestViewingTime}
                        </Typography>
                      </Box>
                    </Box>
                  </MoonCard>
                </Grid>

                {/* Visibility */}
                <Grid item xs={12} sm={6}>
                  <MoonCard title="Visibility" icon={<Visibility />} sx={{ height: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{
                        p: 2, borderRadius: 3, textAlign: 'center',
                        bgcolor: moon.visible ? 'rgba(102,187,106,0.08)' : 'rgba(239,83,80,0.08)',
                        border: `1px solid ${moon.visible ? 'rgba(102,187,106,0.25)' : 'rgba(239,83,80,0.25)'}`,
                      }}>
                        <Typography sx={{ fontSize: 32 }}>{moon.visible ? '👁️' : '☁️'}</Typography>
                        <Typography variant="h6" sx={{
                          fontFamily: 'Orbitron, monospace', fontWeight: 800, mt: 0.5,
                          color: moon.visible ? '#66bb6a' : '#ef5350',
                        }}>
                          {moon.visible ? 'VISIBLE TONIGHT' : 'BELOW HORIZON'}
                        </Typography>
                      </Box>
                      <StatRow label="Altitude" value={`${moon.altitude}°`}       icon={<Height />}   color={moon.altitude > 0 ? '#66bb6a' : '#ef9a9a'} />
                      <StatRow label="Azimuth"  value={`${moon.azimuth}°`}        icon={<Explore />}  />
                      <StatRow label="Observer" value={`${moon.observerLat}°N, ${moon.observerLon}°E`} icon={<MyLocation />} color="rgba(255,236,153,0.5)" />
                    </Box>
                  </MoonCard>
                </Grid>

                {/* Direction & Compass */}
                <Grid item xs={12} sm={6}>
                  <MoonCard title="Direction & Altitude" icon={<Explore />} sx={{ height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                      <CompassCard
                        azimuth={moon.azimuth}
                        direction={moon.direction}
                        altitude={moon.altitude}
                      />
                    </Box>
                  </MoonCard>
                </Grid>

              </Grid>
            </Grid>

            {/* ── Surface Highlights ──────────────────────────────────── */}
            <Grid item xs={12}>
              <MoonCard title="Moon Surface Highlights" icon={<NightsStay />}>
                <Grid container spacing={2}>
                  {SURFACE_FEATURES.map((feature) => (
                    <Grid item xs={12} sm={6} md={4} key={feature.name}>
                      <Tooltip
                        title={feature.description}
                        placement="top"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: '#0d0d0d',
                              border: '1px solid rgba(255,236,153,0.2)',
                              color: '#e3f2fd',
                              maxWidth: 280,
                              fontFamily: 'Exo 2, sans-serif',
                              fontSize: 12,
                              p: 1.5,
                            },
                          },
                        }}
                      >
                        <Box sx={{
                          p: 2, borderRadius: 2,
                          bgcolor: 'rgba(255,236,153,0.03)',
                          border: `1px solid ${feature.color}22`,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: 'rgba(255,236,153,0.07)',
                            borderColor: `${feature.color}55`,
                            transform: 'translateY(-2px)',
                          },
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                            <Typography sx={{ fontSize: 20 }}>{feature.emoji}</Typography>
                            <Typography variant="body2" sx={{
                              color: feature.color, fontWeight: 700,
                              fontFamily: 'Exo 2, sans-serif',
                            }}>
                              {feature.name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label={feature.coords} size="small" sx={{ bgcolor: 'rgba(255,236,153,0.06)', color: 'rgba(255,236,153,0.5)', fontSize: 10 }} />
                            <Chip label={`⌀ ${feature.diameter}`} size="small" sx={{ bgcolor: `${feature.color}15`, color: feature.color, fontSize: 10 }} />
                          </Box>
                        </Box>
                      </Tooltip>
                    </Grid>
                  ))}
                </Grid>
              </MoonCard>
            </Grid>

            {/* ── NASA APOD ───────────────────────────────────────────── */}
            {moon.apod && moon.apod.url && (
              <Grid item xs={12}>
                <MoonCard title="NASA Astronomy Picture of the Day" icon={<NightsStay />}>
                  <Grid container spacing={3} alignItems="flex-start">
                    {moon.apod.media_type === 'image' && (
                      <Grid item xs={12} md={5}>
                        <Box
                          component="img"
                          src={apodImageSrc(moon.apod.url)}
                          alt={moon.apod.title}
                          sx={{
                            width: '100%',
                            borderRadius: 3,
                            border: '1px solid rgba(255,236,153,0.15)',
                            objectFit: 'cover',
                            maxHeight: 320,
                            boxShadow: '0 4px 40px rgba(255,236,153,0.1)',
                          }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </Grid>
                    )}
                    {moon.apod.media_type === 'video' && (() => {
                      const ytMatch = moon.apod.url && moon.apod.url.match(/(?:youtube\.com\/embed\/|youtu\.be\/)([^?&"]+)/);
                      const videoId = ytMatch ? ytMatch[1] : null;
                      return (
                        <Grid item xs={12} md={5}>
                          {videoId ? (
                            <Box
                              component="a"
                              href={`https://www.youtube.com/watch?v=${videoId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'block', position: 'relative', cursor: 'pointer' }}
                            >
                              <Box
                                component="img"
                                src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                                alt={moon.apod.title}
                                sx={{
                                  width: '100%', borderRadius: 3,
                                  border: '1px solid rgba(255,236,153,0.15)',
                                  objectFit: 'cover', maxHeight: 320,
                                  boxShadow: '0 4px 40px rgba(255,236,153,0.1)',
                                  display: 'block',
                                }}
                              />
                              <Box sx={{
                                position: 'absolute', top: '50%', left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 56, height: 56, borderRadius: '50%',
                                bgcolor: 'rgba(0,0,0,0.55)',
                                border: '2px solid rgba(255,236,153,0.6)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                <Typography sx={{ color: '#ffeccc', fontSize: 22, lineHeight: 1, ml: '3px' }}>▶</Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Box
                              component="iframe"
                              src={moon.apod.url}
                              title={moon.apod.title}
                              sx={{
                                width: '100%', height: 280, borderRadius: 3,
                                border: '1px solid rgba(255,236,153,0.15)',
                              }}
                              allowFullScreen
                            />
                          )}
                        </Grid>
                      );
                    })()}
                    <Grid item xs={12} md={moon.apod.media_type !== 'image' && moon.apod.media_type !== 'video' ? 12 : 7}>
                      <Typography variant="h6" sx={{
                        color: '#ffeccc', fontFamily: 'Orbitron, monospace',
                        fontWeight: 700, mb: 1, lineHeight: 1.4,
                      }}>
                        {moon.apod.title}
                      </Typography>
                      {moon.apod.copyright && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,236,153,0.4)', display: 'block', mb: 1.5 }}>
                          © {moon.apod.copyright}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{
                        color: 'rgba(255,236,153,0.6)', fontFamily: 'Exo 2, sans-serif',
                        lineHeight: 1.7, fontSize: 13,
                        display: '-webkit-box', WebkitLineClamp: 7,
                        WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {moon.apod.explanation}
                      </Typography>
                      {moon.apod.date && (
                        <Chip
                          label={moon.apod.date}
                          size="small"
                          sx={{ mt: 2, bgcolor: 'rgba(255,236,153,0.08)', color: 'rgba(255,236,153,0.6)', border: '1px solid rgba(255,236,153,0.15)' }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </MoonCard>
              </Grid>
            )}

            {/* ── Tides Info ──────────────────────────────────────────── */}
            <Grid item xs={12} md={6}>
              <MoonCard title="Tidal Influence" icon={<Public />}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {(() => {
                    const illum  = moon.illumination;
                    const isNew  = illum < 15 || moon.phase === 'New Moon';
                    const isFull = illum > 85 || moon.phase === 'Full Moon';
                    const isQuarter = moon.phase.includes('Quarter');
                    let tideLabel, tideDesc, tideColor;
                    if (isNew || isFull) {
                      tideLabel = 'Spring Tides';
                      tideDesc  = 'Sun, Moon, and Earth aligned — highest high tides and lowest low tides of the month.';
                      tideColor = '#ef5350';
                    } else if (isQuarter) {
                      tideLabel = 'Neap Tides';
                      tideDesc  = 'Sun and Moon at right angles — moderate tidal range, weaker than usual.';
                      tideColor = '#42a5f5';
                    } else {
                      tideLabel = 'Moderate Tides';
                      tideDesc  = 'Tidal forces are intermediate. Check local tide charts for exact times.';
                      tideColor = '#66bb6a';
                    }
                    return (
                      <>
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: `${tideColor}11`, border: `1px solid ${tideColor}33`, textAlign: 'center' }}>
                          <Typography sx={{ fontSize: 28 }}>🌊</Typography>
                          <Typography variant="subtitle1" sx={{ color: tideColor, fontFamily: 'Orbitron, monospace', fontWeight: 700, mt: 0.5 }}>
                            {tideLabel}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255,236,153,0.5)', mt: 0.75, lineHeight: 1.6 }}>
                            {tideDesc}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,236,153,0.3)', textAlign: 'center' }}>
                          ℹ️ Tide prediction is based on moon phase. Consult local tide charts for precise times.
                        </Typography>
                      </>
                    );
                  })()}
                </Box>
              </MoonCard>
            </Grid>

            {/* ── Quick Facts ─────────────────────────────────────────── */}
            <Grid item xs={12} md={6}>
              <MoonCard title="Lunar Quick Facts" icon={<NightsStay />}>
                <Grid container spacing={1}>
                  {[
                    { label: 'Orbital Period',    value: '27.32 days',   emoji: '🔄' },
                    { label: 'Synodic Period',    value: '29.53 days',   emoji: '🌕' },
                    { label: 'Radius',            value: '1,737.4 km',   emoji: '📏' },
                    { label: 'Mass',              value: '7.34 × 10²² kg', emoji: '⚖️' },
                    { label: 'Surface Gravity',   value: '1.62 m/s²',    emoji: '⬇️' },
                    { label: 'Distance (mean)',   value: '384,400 km',   emoji: '📡' },
                  ].map((fact) => (
                    <Grid item xs={6} key={fact.label}>
                      <Box sx={{
                        p: 1.5, borderRadius: 2,
                        bgcolor: 'rgba(255,236,153,0.04)',
                        border: '1px solid rgba(255,236,153,0.07)',
                        height: '100%',
                      }}>
                        <Typography sx={{ fontSize: 18 }}>{fact.emoji}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,236,153,0.4)', display: 'block', mt: 0.5 }}>
                          {fact.label}
                        </Typography>
                        <Typography variant="body2" sx={{
                          color: '#ffeccc', fontFamily: 'Orbitron, monospace',
                          fontWeight: 700, fontSize: 11, mt: 0.25,
                        }}>
                          {fact.value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </MoonCard>
            </Grid>

            {/* ── Footer ──────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', pt: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,236,153,0.2)', fontFamily: 'Exo 2, sans-serif' }}>
                  Ephemeris data computed using Jean Meeus astronomical algorithms · NASA APOD · Updates hourly
                  {moon.lastUpdated && ` · Last updated: ${new Date(moon.lastUpdated).toUTCString()}`}
                </Typography>
              </Box>
            </Grid>

          </Grid>
        )}
      </Box>
    </Box>
  );
}
